import React, { useState, useEffect } from 'react';
import { Button, message, Modal, Select, Tag, Tooltip, notification } from 'antd';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../store/store';
import { currencySignsObj } from '@skygroup/shared/utils/constants';
import useGetGrossPrice from '../../../../hooks/useGetGrossPrice';
import {
  FaCheckCircle,
  FaChevronDown,
  FaClock,
  FaCrown,
  FaTruck,
  FaPrint,
  FaUsers,
  FaHourglassHalf,
} from 'react-icons/fa';
import { AiOutlineInfoCircle } from 'react-icons/ai';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import { roundCurrency } from '@skygroup/shared/utils/roundUpCurrency';
import './ticketCard.less';
import {
  getFirstAvailableQuantity,
  getMaxQuantity,
  getQuantityOptions,
  getFirstAvailableDeliveryMethod,
  getClosestSellingQuantity,
  formatCategoryRestriction,
} from './helpers';
import { HiOutlineTicket } from 'react-icons/hi';
import { getEventTicketCategoryName } from '../../utils/eventTicketHelpers';

interface TicketCardProps {
  ticket: any;
  event: any;
  initialQuantity?: number;
  initialDeliveryMethod?: any;
  isPackageView?: boolean;
  isDynamicPackage?: boolean;
  isPreviewMode?: boolean;
  handleSelectTicket?: ({
    ticket,
    quantity,
    deliveryMethod,
  }: {
    ticket: any;
    quantity: number;
    deliveryMethod: any;
  }) => void;
  handleRemoveTicket?: (ticketId: number) => void;
  onQuantityChange?: (quantity: number, ticketId?: number) => void;
  onDeliveryMethodChange?: (deliveryMethod: any, ticketId?: number) => void;
  renderButtonInside?: boolean;
  isSelected?: boolean;
  hasAnySelected?: boolean;
  totalSelectedCount?: number;
  compactView?: boolean;
  /**
   * Reselect mode: the ticket the user currently holds. When set, the headline
   * price becomes the difference against it — "what does swapping seats cost
   * me?" — instead of the absolute total. Quantity and delivery method are held
   * at the baseline's, so the number reflects the seat change alone and does
   * not move when the user adjusts quantity inside the modal. The breakdown
   * tooltip still shows real absolute figures.
   *
   * Mirrors calcPriceDiff / FlightButton2 in the DP flight reselect.
   */
  priceDiffBaseline?: {
    ticket: any;
    quantity: number;
  };
}

const TicketCard: React.FC<TicketCardProps> = ({
  ticket,
  event,
  initialQuantity,
  initialDeliveryMethod,
  isPackageView = false,
  isDynamicPackage = false,
  isPreviewMode = false,
  handleSelectTicket,
  handleRemoveTicket,
  onQuantityChange,
  onDeliveryMethodChange,
  renderButtonInside = false,
  isSelected = false,
  hasAnySelected = false,
  totalSelectedCount = 0,
  compactView = false,
  priceDiffBaseline,
}) => {
  const {
    sportsAndEvents: t_sportsAndEvents,
    cart: t_cart,
    general: t_general,
  } = useSelector((data: RootState) => data.config.translations);
  const getGrossPrice = useGetGrossPrice();
  const { siteConfig } = useSelector((data: RootState) => data.config.config);

  // Initialize state with props or defaults
  const [selectedQuantity, setSelectedQuantity] = useState<number>(
    initialQuantity !== undefined
      ? initialQuantity
      : getFirstAvailableQuantity(ticket)
  );
  const [selectedDeliveryMethod, setSelectedDeliveryMethod] = useState<any>(
    initialDeliveryMethod !== undefined
      ? initialDeliveryMethod
      : ticket.deliveryMethods && ticket.deliveryMethods.length > 0
      ? ticket.deliveryMethods[0]
      : null
  );
  const [isDescriptionOpen, setIsDescriptionOpen] = useState(false);

  // Update state when props change
  useEffect(() => {
    if (initialQuantity !== undefined) {
      setSelectedQuantity(initialQuantity);
    }
  }, [initialQuantity]);

  useEffect(() => {
    if (initialDeliveryMethod !== undefined) {
      setSelectedDeliveryMethod(initialDeliveryMethod);
    }
  }, [initialDeliveryMethod]);

  useEffect(() => {
    const closestQuantity = getClosestSellingQuantity(
      { availableSellingQuantities: ticket?.availableSellingQuantities },
      selectedQuantity
    );

    if (closestQuantity === null) {
      return;
    }

    setSelectedQuantity(closestQuantity);
    onQuantityChange?.(closestQuantity, ticket.id);
  }, [
    onQuantityChange,
    selectedQuantity,
    ticket?.availableSellingQuantities,
    ticket?.id,
  ]);

  const handleQuantityChange = (quantity: number) => {
    setSelectedQuantity(quantity);

    if (quantity > 0) {
      setSelectedQuantity(quantity);
      onQuantityChange?.(quantity, ticket.id);
    } else {
      message.error(t_sportsAndEvents?.quantityCannotBeZero);
    }
  };

  const handleDeliveryMethodChange = (deliveryMethodId: number) => {
    const method = ticket.deliveryMethods.find(
      (dm: any) => dm.id === deliveryMethodId
    );

    if (method) {
      setSelectedDeliveryMethod(method);
      onDeliveryMethodChange?.(method, ticket.id);
    } else {
      message.error(t_sportsAndEvents?.deliveryMethodNotFound);
    }
  };

  const getButtonText = () => {
    if (isDynamicPackage) {
      if (isSelected) {
        // For selected tickets in dynamic package mode, show "Selected" or "Remove"
        if (totalSelectedCount > 1) {
          return t_cart.remove;
        }
        return t_general.selected;
      } else if (hasAnySelected) {
        // For unselected tickets when others are selected in radio mode, show "Select"
        // This indicates switching behavior (radio button style)
        return t_cart.select;
      } else {
        // First ticket selection
        return t_cart.select;
      }
    } else if (isSelected) {
      return t_general.selected;
    } else if (isPackageView) {
      return t_cart.select;
    } else {
      return t_cart.addToCart;
    }
  };

  const selectTicket = () => {
    handleSelectTicket?.({
      ticket,
      quantity: selectedQuantity,
      deliveryMethod: selectedDeliveryMethod,
    });
  };

  const onButtonClick = () => {
    if (isDynamicPackage && isSelected) {
      // Handle removal in dynamic package mode
      if (totalSelectedCount <= 1) {
        message.warning(t_sportsAndEvents?.atLeastOneTicketMustBeSelected);
        return;
      }
      handleRemoveTicket?.(ticket.id);
    } else if (ticket.purchaseAlert) {
      Modal.confirm({
        title: t_sportsAndEvents?.purchaseAlertTitle,
        icon: <ExclamationCircleOutlined />,
        content: (
          <div style={{ whiteSpace: 'pre-line' }}>{ticket.purchaseAlert}</div>
        ),
        okText: t_sportsAndEvents?.purchaseAlertAgree,
        cancelText: t_sportsAndEvents?.purchaseAlertGoBack,
        onOk: selectTicket,
        okButtonProps: { type: 'primary' },
        width: 520,
        // Sit above the bot chat drawer/panel (z-index 9998–9999) when this
        // ticket is added from the bot; harmless on the normal page.
        zIndex: 10000,
      });
    } else {
      // Handle selection/addition
      selectTicket();
    }
  };

  const renderButton = () => {
    const buttonText = getButtonText();
    const isRemoveButton =
      isDynamicPackage && isSelected && totalSelectedCount > 1;

    return (
      <Button
        type={isRemoveButton ? 'default' : 'primary'}
        size="small"
        onClick={onButtonClick}
        className="btn-style-5"
        danger={isRemoveButton}
        disabled={
          !ticket.id ||
          ticket?.availableSellingQuantities?.length === 0 ||
          (isSelected && !isDynamicPackage) ||
          (isDynamicPackage && isSelected && totalSelectedCount <= 1)
        }
      >
        {buttonText}
      </Button>
    );
  };

  const currencySign =
    currencySignsObj?.[
      ticket.currency?.toLowerCase() as keyof typeof currencySignsObj
    ] ?? ticket.currency;

  const ticketCommission = isDynamicPackage
    ? siteConfig?.shareB2C?.dp
    : ticket?.agent_additional_info?.suggested_commission;

  const unitPrice = getGrossPrice(ticket.price, ticketCommission, 0);
  // Full-precision gross price — totals must be computed from it (not the
  // rounded display price) so they match the cart, which multiplies raw prices.
  const rawUnitPrice = getGrossPrice(ticket.price, ticketCommission, 6);

  const deliveryPrice = selectedDeliveryMethod?.price ?? 0;
  const serviceFee = ticket?.serviceFee ?? 0;
  const totalPrice = roundCurrency(
    rawUnitPrice * selectedQuantity + deliveryPrice + serviceFee,
    2
  );

  // Reselect mode: difference against the ticket the user currently holds.
  // Computed here rather than by the caller so it goes through the same
  // getGrossPrice + commission path as the absolute price and cannot drift.
  // Delivery is excluded because it is held fixed at the baseline's — methods
  // differ per ticket category, so including it would measure two things.
  const baselineTicket = priceDiffBaseline?.ticket;
  const priceDifference = React.useMemo(() => {
    if (!baselineTicket) {
      return null;
    }
    const baselineCommission = isDynamicPackage
      ? siteConfig?.shareB2C?.dp
      : baselineTicket?.agent_additional_info?.suggested_commission;
    const baselineRawUnitPrice = getGrossPrice(
      baselineTicket.price,
      baselineCommission,
      6
    );
    const quantity = priceDiffBaseline?.quantity ?? selectedQuantity;

    return roundCurrency(
      (rawUnitPrice - baselineRawUnitPrice) * quantity +
        (serviceFee - (baselineTicket?.serviceFee ?? 0)),
      2
    );
  }, [
    baselineTicket,
    priceDiffBaseline?.quantity,
    isDynamicPackage,
    siteConfig?.shareB2C?.dp,
    rawUnitPrice,
    serviceFee,
    selectedQuantity,
    getGrossPrice,
  ]);

  const renderHeadlinePrice = () => {
    if (priceDifference === null) {
      return `${currencySign} ${roundCurrency(totalPrice)}`;
    }
    if (priceDifference === 0) {
      return t_general?.sameRates;
    }
    // Sign first, then the magnitude, so RTL locales do not end up rendering
    // something like "₪ 50-".
    return `${priceDifference > 0 ? '+' : '−'} ${currencySign} ${roundCurrency(
      Math.abs(priceDifference)
    )}`;
  };

  const sittingArrangementsText = ticket.sittingArrangements?.trim();
  const isAllTogether = sittingArrangementsText
    ?.toLowerCase()
    .includes('all together');
  const categoryRestrictions = Array.isArray(ticket.categoryRestrictions)
    ? ticket.categoryRestrictions.filter(Boolean)
    : [];

  const splittedCategoryName = ticket.splittedCategoryName;
  const ticketCategoryName = getEventTicketCategoryName(ticket);

  return (
    <div>
      <div
        className={`ticket-item ${
          !handleSelectTicket || isPreviewMode ? '' : 'hover-able'
        } ${isSelected ? 'selected' : ''}`}
      >
        <div className="ticket-info">
          <div className="ticket-name-row">
            <h3 className="ticket-name">
              {ticketCategoryName}
            </h3>
            <div className="category-sections-text">
              {ticket.categorySections?.map((key: any, ii: number) => (
                <span key={key}>
                  {ii > 0 ? ' | ' : ''}
                  {key}
                </span>
              ))}
            </div>
          </div>

          <div className="ticket-tags">
            {splittedCategoryName?.secondary && (
              <Tag>{splittedCategoryName.secondary}</Tag>
            )}
            {ticket.immediateConfirmation && (
              <Tag color="green" icon={<FaCheckCircle />}>
                {t_sportsAndEvents?.instantConfirmation}
                {ticket.immediateConfirmationText && (
                  <Tooltip title={ticket.immediateConfirmationText}>
                    <AiOutlineInfoCircle
                      size={14}
                      className="tag-hint-icon"
                      style={{ marginLeft: '4px', cursor: 'pointer' }}
                    />
                  </Tooltip>
                )}
              </Tag>
            )}
            {ticket.immediateConfirmation === false && (
              <Tag color="orange" icon={<FaHourglassHalf />}>
                {t_sportsAndEvents?.offlineStock}
                {/* Static copy: Events365 sometimes sends the online-stock
                    text (guaranteed confirmation) on offline-stock tickets. */}
                <Tooltip title={t_sportsAndEvents?.offlineStockTooltip}>
                  <AiOutlineInfoCircle
                    size={14}
                    className="tag-hint-icon"
                    style={{ marginLeft: '4px', cursor: 'pointer' }}
                  />
                </Tooltip>
              </Tag>
            )}
            {sittingArrangementsText && (
              <Tag
                color={isAllTogether ? 'green' : 'orange'}
                icon={<FaUsers />}
              >
                {sittingArrangementsText}
              </Tag>
            )}
            {ticket.onHold24Hours?.status && (
              <Tag color="orange" icon={<FaClock />}>
                {t_sportsAndEvents?.hold24Hours}
                {ticket.onHold24Hours?.publicMessage && (
                  <Tooltip title={ticket.onHold24Hours.publicMessage}>
                    <AiOutlineInfoCircle
                      size={14}
                      className="tag-hint-icon"
                      style={{ marginLeft: '4px', cursor: 'pointer' }}
                    />
                  </Tooltip>
                )}
              </Tag>
            )}
            {ticket.isVipPackage && (
              <Tag color="gold" icon={<FaCrown />}>
                {t_sportsAndEvents?.vipPackage}
              </Tag>
            )}
          </div>

          {categoryRestrictions.length > 0 && (
            <div className="ticket-restrictions">
              <ExclamationCircleOutlined className="ticket-restrictions-icon" />
              <div className="ticket-restrictions-list">
                {categoryRestrictions.map((restriction: unknown, index: number) => (
                  <div key={index}>{formatCategoryRestriction(restriction)}</div>
                ))}
              </div>
            </div>
          )}

          {/* {splittedCategoryName?.description && (
            <p className="ticket-description">
              {splittedCategoryName.description}
            </p>
          )} */}
          {splittedCategoryName?.packageDetails && (
            <p className="ticket-description ticket-package-details">
              {splittedCategoryName.packageDetails}
            </p>
          )}
          {compactView ? <HiOutlineTicket size={32} /> : (
            <>
              {ticket.availableSellingQuantities !== undefined && (
                <div className="ticket-availability-text">
                  {getMaxQuantity(ticket)} {t_sportsAndEvents?.ticketsAvailable}
                </div>
              )}
              <div className="ticket-price-calc">
                <Tooltip
                  title={
                    <div className="price-breakdown-tooltip">
                      <div className="breakdown-row">
                        <span>{t_sportsAndEvents?.quantity}</span>
                        <span>{selectedQuantity}</span>
                      </div>
                      <div className="breakdown-row">
                        <span>{t_sportsAndEvents?.pricePerTicket}</span>
                        <span>
                          {currencySign} {roundCurrency(rawUnitPrice, 2)}
                        </span>
                      </div>
                      {deliveryPrice > 0 && (
                        <div className="breakdown-row">
                          <span>{t_sportsAndEvents?.deliveryMethod}</span>
                          <span>
                            {currencySign} {roundCurrency(deliveryPrice, 2)}
                          </span>
                        </div>
                      )}
                      {serviceFee > 0 && (
                        <div className="breakdown-row">
                          <span>{t_sportsAndEvents?.serviceFee}</span>
                          <span>
                            {currencySign} {roundCurrency(serviceFee, 2)}
                          </span>
                        </div>
                      )}
                      <div className="breakdown-row breakdown-total">
                        <span>{t_sportsAndEvents?.totalPrice}</span>
                        <span>
                          {currencySign} {totalPrice}
                        </span>
                      </div>
                    </div>
                  }
                >
                  <div className="price-equation">
                    <span className="calc-qty">{selectedQuantity}</span>
                    <span className="calc-op">×</span>
                    <span className="calc-value">
                      {currencySign} {unitPrice}
                    </span>
                    {deliveryPrice > 0 && (
                      <>
                        <span className="calc-op">+</span>
                        <span className="calc-value calc-delivery">
                          {currencySign} {roundCurrency(deliveryPrice, 2)}
                        </span>
                      </>
                    )}
                    {serviceFee > 0 && (
                      <>
                        <span className="calc-op">+</span>
                        <span className="calc-value calc-service-fee">
                          {currencySign} {roundCurrency(serviceFee, 2)}
                        </span>
                      </>
                    )}
                    <span className="calc-op">=</span>
                    <span
                      className={`calc-total ${
                        priceDifference === null ? '' : 'calc-total-diff'
                      }`}
                    >
                      {renderHeadlinePrice()}
                    </span>
                  </div>
                </Tooltip>
                <div className="price-total-label">
                  {priceDifference === null
                    ? t_sportsAndEvents?.totalPrice
                    : t_sportsAndEvents?.priceDifference}
                </div>
              </div>
              {ticket.availableQuantity !== undefined && (
                <div className="ticket-availability">
                  {t_sportsAndEvents?.available}: {ticket.availableQuantity}
                </div>
              )}
            </>
          )}
        </div>

        {compactView ? null : (
          <div className="ticket-selector">
            <div className="selector-group">
              <div className="selector-item">
                <label className="selector-label">
                  {t_sportsAndEvents?.quantity}
                </label>
                <Select
                  value={selectedQuantity}
                  onChange={handleQuantityChange}
                  options={getQuantityOptions(
                    ticket,
                    isDynamicPackage ? initialQuantity : undefined
                  )}
                  className="ticket-quantity-select"
                  placeholder={t_sportsAndEvents?.selectQuantity}
                />
              </div>

              {ticket.deliveryMethods && ticket.deliveryMethods.length > 0 && (
                <div className="selector-item delivery-method-selector">
                  <label className="selector-label">
                    {t_sportsAndEvents?.deliveryMethod}
                  </label>
                  {ticket.deliveryMethods.length === 1 ? (
                    // If only one delivery method, display it directly
                    <div className="delivery-method-display">
                      {ticket.deliveryMethods[0].name === 'E-Ticket' ? (
                        <FaPrint className="delivery-icon" />
                      ) : (
                        <FaTruck className="delivery-icon" />
                      )}{' '}
                      {ticket.deliveryMethods[0].name}{' '}
                      {ticket.deliveryMethods[0].price > 0
                        ? `(+${
                            currencySignsObj?.[
                              ticket.currency?.toLowerCase() as keyof typeof currencySignsObj
                            ] ?? ticket.currency
                          }${ticket.deliveryMethods[0].price})`
                        : `(${t_sportsAndEvents?.deliveryFree})`}
                    </div>
                  ) : (
                    // If multiple delivery methods, show select dropdown
                    <Select
                      value={
                        selectedDeliveryMethod?.id ||
                        getFirstAvailableDeliveryMethod(ticket)
                      }
                      onChange={handleDeliveryMethodChange}
                      className="delivery-method-select"
                      placeholder={t_sportsAndEvents?.selectDeliveryMethod}
                    >
                      {ticket.deliveryMethods.map((method: any) => (
                        <Select.Option key={method.id} value={method.id}>
                          {method.name === 'E-Ticket' ? (
                            <FaPrint className="delivery-icon" />
                          ) : (
                            <FaTruck className="delivery-icon" />
                          )}{' '}
                          {method.name}{' '}
                          {method.price > 0
                            ? `(+${
                                currencySignsObj?.[
                                  ticket.currency?.toLowerCase() as keyof typeof currencySignsObj
                                ] ?? ticket.currency
                              }${method.price})`
                            : `(${t_sportsAndEvents?.deliveryFree})`}
                        </Select.Option>
                      ))}
                    </Select>
                  )}
                </div>
              )}

              {renderButtonInside && (
                <div
                  className="render-button-inside"
                  style={{ display: 'flex', justifyContent: 'flex-end' }}
                >
                  {renderButton()}
                </div>
              )}
            </div>
          </div>
        )}

        {(splittedCategoryName.description ||
          selectedDeliveryMethod?.description) && (
          <div className="ticket-footer">
            <button
              type="button"
              className="ticket-footer-toggle"
              onClick={() => setIsDescriptionOpen(!isDescriptionOpen)}
            >
              {isDescriptionOpen
                ? t_sportsAndEvents?.hideDescription
                : t_sportsAndEvents?.showDescription}
              <FaChevronDown
                className={`toggle-icon ${isDescriptionOpen ? 'open' : ''}`}
              />
            </button>
            <div
              className={`ticket-footer-content ${
                isDescriptionOpen ? 'open' : ''
              }`}
            >
              <div className="ticket-footer-content-inner">
                {splittedCategoryName.description && (
                  <p className="ticket-description">
                    {splittedCategoryName.description}
                  </p>
                )}
                {selectedDeliveryMethod?.description && (
                  <div className="delivery-method-description">
                    <b>{t_sportsAndEvents?.deliveryMethodDescription}</b>
                    <p className="ticket-description">
                      {selectedDeliveryMethod.description}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {!renderButtonInside && (
        <div
          style={{
            display: !handleSelectTicket || isPreviewMode ? 'none' : 'flex',
            justifyContent: 'flex-end',
            marginTop: '12px',
          }}
        >
          {renderButton()}
        </div>
      )}
    </div>
  );
};

export default TicketCard;
